import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { type ChainDetail, type ChainLoadOut, useChainDetail } from "@/api";
import { ChainMap } from "@/components/ChainMap";
import { CostCalculator } from "@/components/CostCalculator";
import { Tabs, type TabItem } from "@/components/Tabs";
import { Timeline } from "@/components/Timeline";

type TabKey = "map" | "schedule" | "timeline" | "cost";

export function ChainDetailPage(): JSX.Element {
  const { chainId } = useParams<{ chainId: string }>();
  const { data, isLoading, isError, error } = useChainDetail(chainId);
  const [tab, setTab] = useState<TabKey>("map");

  if (isLoading) {
    return <div className="card p-6 text-slate-500">Loading chain detail…</div>;
  }
  if (isError) {
    return (
      <div className="card p-6 text-rose-600">
        Failed to load chain: {error instanceof Error ? error.message : "unknown error"}
      </div>
    );
  }
  if (!data) return <></>;

  const tabs: TabItem<TabKey>[] = [
    { key: "map", label: "Route map", content: <ChainMap chain={data} /> },
    {
      key: "schedule",
      label: "Schedule",
      badge: data.loads.length,
      content: <ScheduleView chain={data} />,
    },
    {
      key: "timeline",
      label: "Timeline",
      content: <Timeline chain={data} />,
    },
    {
      key: "cost",
      label: "Cost",
      content: <CostCalculator chain={data} />,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Link to="/chains" className="text-sm text-slate-500 hover:text-slate-900">
          ← back to chains
        </Link>
        <span className="font-mono text-xs text-slate-400">{data.chain_id}</span>
      </div>

      <SummaryHeader chain={data} />

      <Tabs<TabKey> items={tabs} active={tab} onChange={setTab} />
    </div>
  );
}

// ────────────────────────────────────────────────────────  Header  ──

function SummaryHeader({ chain }: { chain: ChainDetail }): JSX.Element {
  return (
    <header className="card p-5">
      <h1 className="text-2xl font-semibold text-slate-900">
        {chain.origin_market} → {chain.destination_market}{" "}
        <span className="text-base font-medium text-slate-500">(k = {chain.k})</span>
      </h1>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Stat label="Total miles" value={`${Math.round(chain.total_miles).toLocaleString()} mi`} />
        <Stat
          label="Deadhead"
          value={`${Math.round(chain.total_deadhead_mi).toLocaleString()} mi`}
        />
        <Stat label="Drive time" value={fmtHours(chain.total_drive_time_min)} />
        <Stat label="Risk" value={chain.risk_tier} />
        <Stat
          label="First pickup"
          value={new Date(chain.first_pickup_at).toLocaleString()}
        />
        <Stat
          label="Last drop"
          value={new Date(chain.last_drop_at).toLocaleString()}
        />
        <Stat label="Min slack" value={fmtMinutes(chain.min_slack_min)} />
        <Stat label="Duration" value={`${chain.duration_days.toFixed(1)} days`} />
      </div>
    </header>
  );
}

// ────────────────────────────────────────────────────────  Schedule  ──

function ScheduleView({ chain }: { chain: ChainDetail }): JSX.Element {
  return (
    <ul className="space-y-2">
      {chain.loads.map((leg) => (
        <li key={`${leg.seq}-${leg.load_id}`} className="card p-4 text-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-mono text-slate-500">seq {leg.seq}</span>{" "}
              <span className="font-semibold">load #{leg.load_id}</span>
            </div>
            <div className="text-slate-500 text-xs">
              drive {fmtMinutes(leg.drive_time_min)}
            </div>
          </div>
          <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-slate-600">
            <Stat label="Pickup" value={new Date(leg.pickup_at).toLocaleString()} compact />
            <Stat label="Drop" value={new Date(leg.drop_at).toLocaleString()} compact />
            <PolylineSummary
              label="Loaded polyline"
              points={leg.loaded_polyline.length}
            />
            <PolylineSummary
              label="Deadhead polyline"
              points={leg.deadhead_polyline.length}
            />
            {leg.deadhead_mi != null && (
              <Stat label="Deadhead" value={`${Math.round(leg.deadhead_mi)} mi`} compact />
            )}
            {leg.slack_min != null && (
              <Stat label="Slack" value={fmtMinutes(leg.slack_min)} compact />
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function PolylineSummary({
  label,
  points,
}: {
  label: string;
  points: number;
}): JSX.Element {
  return (
    <Stat
      label={label}
      value={points === 0 ? "—" : `${points} pts`}
      compact
    />
  );
}

// ────────────────────────────────────────────────────────  Bits  ──

interface StatProps {
  label: string;
  value: string;
  compact?: boolean;
}

function Stat({ label, value, compact }: StatProps): JSX.Element {
  return (
    <div>
      <div className={compact ? "text-slate-400" : "text-xs text-slate-400"}>{label}</div>
      <div className={compact ? "text-slate-900" : "font-medium text-slate-900"}>{value}</div>
    </div>
  );
}

function fmtMinutes(min: number): string {
  if (min < 60) return `${Math.round(min)} min`;
  return `${(min / 60).toFixed(1)} h`;
}

function fmtHours(min: number): string {
  return `${(min / 60).toFixed(1)} h`;
}

// Keep ChainLoadOut imported (used by ChainMap consumers downstream).
export type { ChainLoadOut };
